import { Box, Divider, ListItem, Typography } from '@material-ui/core';
import { GridRowParams } from '@material-ui/data-grid';
import { Api } from 'api';
import VMDataGrid from 'components/main/VMDataGrid';
import TextLink from 'components/other/TextLink';
import ActionsMenu from 'components/other/ActionsMenu';
import { TransactionSelectionModal } from 'components/modals/TransactionSelectionModal';
import { ReservationReleaseModal } from 'components/modals/ReservationReleaseModal';
import { observer } from 'mobx-react-lite';
import { formatToSloDate } from 'pages/Sessions/SessionsPage';
import { ReactElement, useState } from 'react';
import { useProvider } from 'state/providerStore';
import { gridColumn } from 'utils/gridUtils';
import {
  userDetailsTabParameters,
  companyDetailsParameters,
  walletDetailsParameters,
} from 'utils/routing';
import PersonIcon from '@material-ui/icons/Person';
import BusinessIcon from '@material-ui/icons/Business';
import { formatStringValue } from 'utils/utils';

const getOpenedBalancesColumns = () => [
  gridColumn('user', {
    headerName: 'User',
    flex: 1,
    minWidth: 200,
    renderCell: (params: GridRowParams) => {
      const { row } = params;
      let to = '';
      let type: ReactElement<any, any>;
      if (row.userId) {
        to = userDetailsTabParameters(row.userId);
        type = <PersonIcon fontSize="small" color="primary" />;
      } else if (row.accountId) {
        to = companyDetailsParameters(row.accountId);
        type = <BusinessIcon fontSize="small" color="primary" />;
      }
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', gridColumnGap: '0.5rem' }}>
          {type}
          <TextLink label={row.ownerName || 'N/A'} to={to} />
        </Box>
      );
    },
  }),
  gridColumn('settlementMode', {
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      if (!params.row.settlementMode) {
        return '';
      }
      return formatStringValue(params.row.settlementMode);
    },
  }),
  gridColumn('lastTransaction', {
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      const date = new Date(params.row.lastEntry?.createdAt);

      if (!date) {
        return <div />;
      }

      const sloDate = formatToSloDate(date);

      return <div>{sloDate}</div>;
    },
  }),
  gridColumn('lastInvoice', {
    flex: 1,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      const date = new Date(params.row.lastInvoice?.createdAt);

      if (Number.isNaN(date.getTime())) {
        return <div>No invoice</div>;
      }

      const sloDate = formatToSloDate(date);

      return <div>{sloDate || ''}</div>;
    },
  }),
  gridColumn('balance', {
    flex: 0.7,
    minWidth: 150,
    renderCell: (params: GridRowParams) => {
      const { row } = params;
      const formattedBalance = row.lastEntry?.balance?.formatted;
      const walletId = row.lastEntry?.walletId;
      return <TextLink label={formattedBalance} to={walletDetailsParameters(walletId)} />;
    },
  }),
  gridColumn(' ', {
    width: 60,
    align: 'left',
    headerAlign: 'left',
    renderCell: () => null, // Will be overridden in the component
  }),
];

export const OpenedBalancesPage = observer(() => {
  const debtProvider = useProvider(Api.billing.getDebt);
  const [invoiceModalState, setInvoiceModalState] = useState<{
    open: boolean;
    walletId: string;
    ownerName: string;
  }>({ open: false, walletId: '', ownerName: '' });
  const [releaseModalState, setReleaseModalState] = useState<{
    open: boolean;
    walletId: string;
    ownerName: string;
  }>({ open: false, walletId: '', ownerName: '' });

  const handleOpenInvoiceModal = (walletId: string, ownerName: string) => {
    setInvoiceModalState({ open: true, walletId, ownerName });
  };

  const handleCloseInvoiceModal = () => {
    setInvoiceModalState({ open: false, walletId: '', ownerName: '' });
  };

  const handleOpenReleaseModal = (walletId: string, ownerName: string) => {
    setReleaseModalState({ open: true, walletId, ownerName });
  };

  const handleCloseReleaseModal = () => {
    setReleaseModalState({ open: false, walletId: '', ownerName: '' });
  };

  const handleSuccess = () => {
    debtProvider.reloadResource();
  };

  const balanceActions = (params: GridRowParams) => {
    const { row } = params;
    const walletId = row.lastEntry?.walletId;
    const ownerName = row.ownerName || 'Unknown';

    if (!walletId) return null;

    return (
      <ActionsMenu>
        <ListItem button onClick={() => handleOpenInvoiceModal(walletId, ownerName)}>
          <Typography style={{ width: '100%' }}>Generate Invoice</Typography>
        </ListItem>
        <Divider />
        <ListItem button onClick={() => handleOpenReleaseModal(walletId, ownerName)}>
          <Typography style={{ width: '100%' }}>Release Funds</Typography>
        </ListItem>
      </ActionsMenu>
    );
  };

  const columns = getOpenedBalancesColumns().map((col) => {
    if (col.field === ' ') {
      return {
        ...col,
        renderCell: (params: GridRowParams) => balanceActions(params),
      };
    }
    return col;
  });

  return (
    <div style={{ width: '100%' }}>
      <VMDataGrid label="Opened Balances" rows={debtProvider.data} columns={columns} />
      <TransactionSelectionModal
        open={invoiceModalState.open}
        walletId={invoiceModalState.walletId}
        ownerName={invoiceModalState.ownerName}
        onClose={handleCloseInvoiceModal}
        onSuccess={handleSuccess}
      />
      <ReservationReleaseModal
        open={releaseModalState.open}
        walletId={releaseModalState.walletId}
        ownerName={releaseModalState.ownerName}
        onClose={handleCloseReleaseModal}
        onSuccess={handleSuccess}
      />
    </div>
  );
});
